// The single source of truth for which languages the dashboard offers (VLT-2824).
//
// Before this existed the list was hardcoded in six places (i18n resources, the Icon flag registry,
// and a switch plus an options array in BOTH App.tsx and Login.tsx). Keeping six copies in step by
// hand is why locale files could sit in the repo without ever becoming selectable.

// Locale JSON is synced from Localazy (see localazy.json), so the set of files on disk IS the set of
// languages Voltie Local has. Globbing them means a newly synced file needs no change here, and a
// language listed below whose file is missing can never reach a dropdown.
const localeModules = import.meta.glob('./assets/locales/*.json', { eager: true }) as Record<
  string,
  { default: Record<string, string> }
>

export const resources = Object.fromEntries(
  Object.entries(localeModules).map(([path, mod]) => [
    path.split('/').pop()!.replace('.json', ''),
    { translation: mod.default },
  ]),
)

export interface Language {
  /** i18next code, identical to the locale filename. */
  code: string
  /** Native name. Doubles as the key the Dropdown reports back, since it has no separate value. */
  label: string
}

const ALL: Language[] = [
  { code: 'cs', label: 'Čeština' },
  { code: 'da', label: 'Dansk' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
  { code: 'es', label: 'Español' },
  { code: 'fr', label: 'Français' },
  { code: 'hr', label: 'Hrvatski' },
  { code: 'it', label: 'Italiano' },
  { code: 'lt', label: 'Lietuvių' },
  { code: 'hu', label: 'Magyar' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'no', label: 'Norsk' },
  { code: 'pl', label: 'Polski' },
  { code: 'pt', label: 'Português' },
  { code: 'ro', label: 'Română' },
  { code: 'sk', label: 'Slovenčina' },
  { code: 'sl', label: 'Slovenščina' },
  { code: 'fi', label: 'Suomi' },
  { code: 'sv', label: 'Svenska' },
  { code: 'tr', label: 'Türkçe' },
  { code: 'ar', label: 'العربية' },
  { code: 'ru', label: 'Русский' },
  { code: 'uk', label: 'Українська' },
]

export const LANGUAGES: Language[] = ALL
  .filter(language => language.code in resources)
  .sort((a, b) => a.label.localeCompare(b.label))

export const languageByLabel = (label: string) => LANGUAGES.find(language => language.label === label)
export const languageByCode = (code: string) => LANGUAGES.find(language => language.code === code)

/** Registry key for the language's flag, e.g. `flag.SV` is the Swedish entry (a Swedish flag). */
export const flagIcon = (code: string) => `flag.${code.toUpperCase()}`
